/**
 * Session-scoped store for agent-computed trend series that no historian
 * entry backs; a saved record can open as a Process Data tab or route and
 * survives a refresh within the browser tab.
 */

export interface ComputedTrendSeries {
  entryId: string
  label: string
  units?: string
  baseEntryId?: string
  points: [string, number | null][]
}

export interface ComputedTrendMarker {
  entryId: string
  timestamp: string
  value: number
  /** When the citation was clicked; a new click re-seeds a deleted marker. */
  requestedAt?: number
}

export interface ComputedTrendRecord {
  key: string
  siteId: string
  label: string
  startDatetime?: string
  endDatetime?: string
  series: ComputedTrendSeries[]
  /** Seeded as workbench pens; the first present pen anchors the y-scale. */
  contextEntryIds?: string[]
  marker?: ComputedTrendMarker
  /** Per-pen y-axis bounds the agent asked for; absent means auto-scale. */
  customAxisRanges?: Record<string, { min?: number; max?: number }>
  /** One axis for every pen with these bounds; absent keeps per-pen axes. */
  sharedAxisRange?: { min?: number; max?: number }
  /** Pens the agent hid; seeded hidden on the workbench, user toggles win after. */
  hiddenEntryIds?: string[]
  /** Bin size the agent asked for, in the backend's plural units. */
  interval?: number
  intervalUnit?: string
}

/**
 * The derived_ prefix routes every existing derived-pen branch; the
 * computed_ segment marks pens whose gaps are semantic.
 */
export const COMPUTED_PEN_PREFIX = 'derived_computed_'

export function computedPenId(entryId: string): string {
  return COMPUTED_PEN_PREFIX + entryId.replace(/[^a-zA-Z0-9]/g, '_')
}

/** Whether an id names a column the agent computed (a computed or totalizer-change series) rather than a stored tag. */
export function isComputedColumnId(entryId: string): boolean {
  return entryId.startsWith('computed:') || entryId.startsWith('delta:')
}

/** The chart pen id for an agent-facing id: computed columns map to their derived pen, tags stay as they are. */
export function chartPenId(entryId: string): string {
  return isComputedColumnId(entryId) ? computedPenId(entryId) : entryId
}

const UPDATE_EVENT = 'gecko:computed-trend-updated'

export function subscribeComputedTrendUpdates(
  key: string,
  onUpdate: () => void
): () => void {
  const listener = (event: Event) => {
    if ((event as CustomEvent<{ key?: string }>).detail?.key === key) {
      onUpdate()
    }
  }
  window.addEventListener(UPDATE_EVENT, listener)
  return () => window.removeEventListener(UPDATE_EVENT, listener)
}

const STORE_PREFIX = 'gecko_computed_trend_v1'
const INDEX_KEY = `${STORE_PREFIX}:index`
const MAX_RECORDS = 8

export function computedTrendKey(
  siteId: string,
  entryIds: readonly string[],
  window?: { start?: string; end?: string }
): string {
  const material = [
    siteId,
    [...entryIds].sort().join(','),
    window?.start ?? '',
    window?.end ?? '',
  ].join('|')
  let hash = 5381
  for (let index = 0; index < material.length; index++) {
    hash = (hash * 33) ^ material.charCodeAt(index)
  }
  return (hash >>> 0).toString(36)
}

function recordStorageKey(key: string): string {
  return `${STORE_PREFIX}:${key}`
}

function loadIndex(): string[] {
  try {
    const raw = sessionStorage.getItem(INDEX_KEY)
    const parsed: unknown = raw ? JSON.parse(raw) : []
    return Array.isArray(parsed)
      ? parsed.filter((item): item is string => typeof item === 'string')
      : []
  } catch {
    return []
  }
}

export function saveComputedTrend(record: ComputedTrendRecord): void {
  try {
    sessionStorage.setItem(recordStorageKey(record.key), JSON.stringify(record))
    const index = [record.key, ...loadIndex().filter((k) => k !== record.key)]
    for (const evicted of index.slice(MAX_RECORDS)) {
      sessionStorage.removeItem(recordStorageKey(evicted))
    }
    sessionStorage.setItem(
      INDEX_KEY,
      JSON.stringify(index.slice(0, MAX_RECORDS))
    )
    window.dispatchEvent(
      new CustomEvent(UPDATE_EVENT, { detail: { key: record.key } })
    )
  } catch {
    // Storage unavailable: only refresh continuity is lost.
  }
}

export function loadComputedTrend(key: string): ComputedTrendRecord | null {
  try {
    const raw = sessionStorage.getItem(recordStorageKey(key))
    if (!raw) return null
    const parsed: unknown = JSON.parse(raw)
    if (
      typeof parsed !== 'object' ||
      parsed === null ||
      !('series' in parsed) ||
      !Array.isArray((parsed as { series: unknown }).series)
    ) {
      return null
    }
    return parsed as ComputedTrendRecord
  } catch {
    return null
  }
}
